import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useNavigate } from 'react-router-dom';
import { loadStripe, Stripe, StripeElementsOptions, Appearance } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import AppPageContainer from "@/components/AppPageContainer";

import { billingSetupIntent, billingSavePaymentMethod, clearSubscriptionSuccessStatus, billingSubscribe, billingUnsubscribe, billingSaveIntegration } from '@/store/billing';
// import { subscribeAndReturnUser, unsubscribeAndReturnUser } from '@/store/thunks';

import AppCheckoutForm from '@/components/AppCheckoutForm';
import AppLoader from '@/components/AppLoader';
import BillingInfo from '@/components/BillingInfo';
import AppBox from '@/components/AppBox';
import AppText from '@/components/AppText';
import AppLink from '@/components/AppLink';
import AppModal from '@/components/AppModal';
import AfterRegisterModal from '@/components/Modals/AfterRegisterModal';
import AppCard from "@/components/AppCard"
import AppInput from "@/components/AppInput";
import AppButton from "@/components/AppButton";
const Billing: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // State
  const { me } = useAppSelector((state) => state.auth);
  const { intent } = useAppSelector((state) => state.billing);

  const [clientSecret, setClientSecret] = useState<string>('');
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [showCheckoutForm, setShowCheckoutForm] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isOnSubscription, setIsOnSubscription] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [formIntegration, setFormIntegration] = useState({
    client_id: "",
    client_secret: "",
  });
  const [errorsIntegration, setErrorsIntegration] = useState({
    client_id: "",
    client_secret: "",
  });

  // Load Stripe.js
  useEffect(() => {
    const loadStripeInstance = () => {
      const promise = loadStripe(import.meta.env.VITE_STRIPE_KEY);
      setStripePromise(promise);
    };
    loadStripeInstance();
  }, []);

  // Initialize client secret for Stripe
  useEffect(() => {
    if (!intent.id) {
      dispatch(billingSetupIntent());
    } else {
      setClientSecret(intent.client_secret || '');
    }
  }, [intent, dispatch]);

  // Handle user subscription status
  useEffect(() => {
    if (me?.id) {
      setShowCheckoutForm(!me.isSubscribed);
      setIsOnSubscription(me.isSubscribed && !me.isCanceled);
      setIsLoading(false);

      
      // if (me.integrations.length > 0 && me.integrations[0].settings && me.isSubscribed) {
      //   // console.log(JSON.parse(me.integrations[0].settings));
      //   const settings = JSON.parse(me.integrations[0].settings)
      //   setIsOpenModal(!settings.client_id);
      // }
    }
  }, [me]);

  // Handlers
  const handleShowCheckoutForm = () => setShowCheckoutForm((prev) => !prev);

  const handleCancelSubscription = async () => {
    await dispatch(billingUnsubscribe());
  };

  const handleSubscribe = async (paymentMethod: string) => {
    await dispatch(billingSavePaymentMethod({ payment_method: paymentMethod }));
    const res = await dispatch(billingSubscribe());
    
    if (res.payload) {
      setIsOpenModal(true);
    }
  };

  const handleNavigateToDashboard = () => {
    setIsOpenModal(false);
    dispatch(clearSubscriptionSuccessStatus());
    navigate(me?.is_agency_admin ? '/agency' : '/');
  };

  const appearance: Appearance = {
    theme: 'stripe', // Literal value allowed by Stripe
    variables: {
      colorPrimary: '#0570de',
      fontFamily: 'Arial, sans-serif',
    },
  };

  // Stripe options
  const stripeOptions: StripeElementsOptions = {
    appearance,
    clientSecret,
  };

  // Common JSX
  const subscriptionStatus = (
    <AppBox column>
      <AppText>Subscription status:</AppText>
      <AppText color={isOnSubscription ? 'success' : 'error'}>
        {isOnSubscription ? 'Subscribed' : 'Not Subscribed'}
      </AppText>
    </AppBox>
  );

  const handleChangeIntegrationInput =
    (field: keyof typeof formIntegration) =>
    (value: string): void => {
      if (errorsIntegration[field]) setErrorsIntegration((prev) => ({ ...prev, [field]: "" }));
      setFormIntegration((prev) => ({ ...prev, [field]: value }));
    };

  const handleSubmitIntegration = async () => {
    if (formIntegration.client_id && formIntegration.client_secret) { 
      await dispatch(billingSaveIntegration({
        client_id: formIntegration.client_id,
        client_secret: formIntegration.client_secret,
      }));
      handleNavigateToDashboard();
    } else {
      setErrorsIntegration((prev) => ({ ...prev, client_id: "Client ID is required", client_secret: "Client Secret is required" }));
    }
  };

  // Add loading state check
  if (!me) {
    return null; // or return a loading spinner
  }

  return (
    <AppPageContainer title="Billing">
      {me.is_live ? (
        subscriptionStatus
      ) : (
        <AppCard className="gap-4">
          {subscriptionStatus}
          
          {showCheckoutForm && clientSecret && stripePromise ? (
            <Elements key={clientSecret} options={stripeOptions} stripe={stripePromise}>
              <AppCheckoutForm
                clientSecret={clientSecret}
                isOnSubscription={isOnSubscription}
                onSubscribe={handleSubscribe}
                onSwitchToInfo={handleShowCheckoutForm}
              />
            </Elements>
          ) : (
            <BillingInfo
              onClickChange={handleShowCheckoutForm}
              onCancelSubscription={handleCancelSubscription}
            />
          )}
    
          <AppBox column gap={2}>
            <AppText variant="caption">
              We offer a 30 day free trial - cancel anytime, no questions asked. You can cancel from inside your account, or by calling or emailing us.
            </AppText>
            
            <AppBox align="center" gap={2}>
              <AppText variant="caption">
                Book a call with us here:
              </AppText>
              <AppLink to="https://calendly.com/lsarespond" target="_blank" className='text-sm'>
                calendly.com/lsarespond
              </AppLink>
            </AppBox>
          </AppBox>
    
          {/* <AppModal 
            isOpen={isOpenModal} 
            title="Congratulations!" 
            onClose={handleNavigateToDashboard}
          >
            <AfterRegisterModal onConfirm={handleNavigateToDashboard} />
          </AppModal> */}

          {/* <AppModal 
            isOpen={isOpenModal} 
            title="Final step" 
            onClose={() => setIsOpenModal(false)}
            preventOutsideClick
            hideCloseButton
          >
            <AppBox column gap={4}>
              <AppText>In order to use integration with ServiceFusion, please enter your API credentials below:</AppText>

            <AppInput
              type="text"
              label="Service Fusion Client ID"
              fullWidth
              onChange={handleChangeIntegrationInput("client_id")}
              value={formIntegration.client_id}
              error={errorsIntegration.client_id}
              onPressEnter={handleSubmitIntegration}
            />
            <AppInput
              type="text"
              label="Service Fusion Client Secret"
              fullWidth
              onChange={handleChangeIntegrationInput("client_secret")}
              value={formIntegration.client_secret}
              error={errorsIntegration.client_secret}
              onPressEnter={handleSubmitIntegration}
              />
              <AppButton onClick={handleSubmitIntegration}>Submit</AppButton>
            </AppBox>
          </AppModal> */}
        </AppCard>
      )}
    </AppPageContainer>
  )
};

export default Billing;